import z from 'zod';
import { heroAppIdSchema } from '../../common/app-registry.schema';
import { conversationChannelTypeSchema } from '../conversation/conversation.model';
import { dateSchema, objectIdSchema } from '../../validation';

/**
 * How the relationship started.
 *
 * One field instead of a `direction` boolean plus a guess, because who
 * moved first and what they did are the same fact: a call we placed and
 * a message they sent are not two values of one dimension, they are two
 * different events with different follow-ups.
 *
 * - `inbound_message`  — they messaged us (WhatsApp, in-app chat).
 * - `outbound_message` — we messaged them. Today the agent's own sends;
 *   the bulk campaign sender lands here too when it ships.
 * - `inbound_call`     — they phoned us.
 * - `outbound_call`    — a rep phoned them.
 * - `self_signup`      — they signed themselves up in the app or on the
 *   web without talking to anyone first. Not "no first touch": the
 *   signup *is* the first touch, and it was previously indistinguishable
 *   from an inbound message.
 * - `form`             — a landing-page / demo form submission.
 *
 * Replaces the old `direction` enum, which had to be *inferred* from
 * whether a `cold_reach` funnel step existed — and only one product's
 * funnel ever declared that step, so every Hero outbound read as
 * inbound. {@link leadFirstTouchDirection} still answers the coarser
 * "did we chase them" question for reports, derived rather than stored.
 */
export const leadFirstTouchKindSchema = z.enum([
  'inbound_message',
  'outbound_message',
  'inbound_call',
  'outbound_call',
  'self_signup',
  'form',
]);
export type LeadFirstTouchKind = z.infer<typeof leadFirstTouchKindSchema>;

/** Coarse direction, for reports that group by "did we chase them". */
export const leadDirectionSchema = z.enum(['inbound', 'outbound']);
export type LeadDirection = z.infer<typeof leadDirectionSchema>;

/**
 * Which way round a first touch went.
 *
 * Derived, never stored — the kind already carries it, and a stored copy
 * is a copy that can disagree.
 */
export const leadFirstTouchDirection = (kind: LeadFirstTouchKind): LeadDirection =>
  kind === 'outbound_message' || kind === 'outbound_call' ? 'outbound' : 'inbound';

/**
 * The first contact with a lead, recorded once and never rewritten.
 *
 * Every field answers a question the CRM list asks in a column: "how did
 * this start", "on which of our numbers", "how long ago", "did they ever
 * answer". Before this, none of them had an answer — a WhatsApp lead's
 * row said `source: whatsapp` and stopped.
 *
 * Deliberately NOT here:
 *
 * - **`product`** — derivable from {@link app} through `APP_REGISTRY`,
 *   and `funnels[].product` is the authority on progress. A third copy
 *   was a third thing to keep in step.
 * - **`direction`** — see {@link leadFirstTouchDirection}.
 */
export const leadFirstTouchSchema = z.object({
  /** When the first message / call / signup happened. */
  at: dateSchema,
  /** How it started. See {@link leadFirstTouchKindSchema}. */
  kind: leadFirstTouchKindSchema,
  /**
   * The surface it happened on — `whatsapp`, `in_app`, `voice`, …
   *
   * Deliberately `ConversationChannelType` and not `AppSurface`: this
   * describes a conversation, and it is the value
   * `Conversation.channel.type` already carries, so the two cannot
   * disagree about a thread that exists. Optional because a form
   * submission and a self-signup happen on no conversation channel.
   */
  channel: conversationChannelTypeSchema.optional(),
  /**
   * Which Hero app was involved — `wathiqa_plus_agent`, `hero_agent`,
   * `landing`, …
   *
   * The brand, the surface, and the product are all derivable from it
   * through `APP_REGISTRY`, which is why none of them is stored beside
   * it.
   */
  app: heroAppIdSchema.optional(),
  /**
   * The thread it started in, so the transcript is one click away.
   *
   * The *first* thread only. `Conversation.leadId` is the reverse link
   * and carries every thread the lead has — which is what the drawer's
   * Chat tab lists, and why the duplicate that used to sit on
   * `sourceMeta.conversationId` is gone.
   */
  conversationId: objectIdSchema.optional(),
  /**
   * When they first answered us.
   *
   * Only meaningful on an outbound first touch, where the gap between
   * "we sent" and "they replied" is the only measure of whether the
   * outreach worked. An outbound lead with no `repliedAt` never
   * answered; on an inbound first touch it equals {@link at}.
   */
  repliedAt: dateSchema.optional(),
});
export type LeadFirstTouch = z.infer<typeof leadFirstTouchSchema>;

/**
 * The most recent contact — the same shape as a first touch, minus the
 * fields that only make sense once.
 *
 * `repliedAt` is not here: on the latest touch it would either equal
 * `at` (they spoke) or duplicate what `kind` already says (we spoke, no
 * answer yet). Neither is worth a field.
 *
 * Kept as its own schema rather than reusing `leadFirstTouchSchema`
 * because the two answer different questions and will drift: attribution
 * wants the campaign and the surface, a work queue wants recency and
 * direction. Sharing the object would have every field added for one
 * silently appear on the other.
 */
export const leadLastTouchSchema = z.object({
  /** When it happened. */
  at: dateSchema,
  /** What happened. Same vocabulary as the first touch. */
  kind: leadFirstTouchKindSchema,
  /** The surface it happened on. */
  channel: conversationChannelTypeSchema.optional(),
  /** Which Hero app / line it happened on. */
  app: heroAppIdSchema.optional(),
  /** The thread it happened in — the latest one, not the first. */
  conversationId: objectIdSchema.optional(),
});
export type LeadLastTouch = z.infer<typeof leadLastTouchSchema>;
